import update from "immutability-helper";
import { get } from "lodash";

import {
  INITIATE,
  INITIATE_ZONE,
  CLEAR_ZONE_EVENTS,
  SET_TIMEOUT,
  ADD_EVENT,
  OPEN_CARD,
  SET_OPEN,
  ADD_DAC_DETAILS,
  UPDATE_EVENTS,
} from "../../constants/actionTypes";
import { driveInOut, trainInOut, walkInOut } from "../../constants/reducers";
import {
  detected,
  absent,
  identified,
  driver,
  truck,
  trailer,
  container,
  unidentified,
  supplemental,
  thermal,
  state_reset,
} from "../../constants/common";

const initialZoneState = {
  driveInOut: {
    globalState: 0,
    startTime: 0,
    driver: { state: absent, id: 0, profile: {} },
    truck: { state: absent, id: 0, profile: {} },
    trailer: { state: absent, id: 0, profile: {} },
    container: { state: absent, id: 0, profile: {} },
  },
  walkInOut: {
    events: {},
  },
  trainInOut: {
    events: {},
  }
};

const liveEvents = (state = {}, action) => {
  switch (action.type) {
    case INITIATE:
      return {
        defaultTimer: action.defaultTimer,
      };
    case INITIATE_ZONE:
      if (!state[action.zoneId]) {
        return {
          ...state,
          [action.zoneId]: initialZoneState[action.name],
        };
      } else {
        return state;
      }
    case CLEAR_ZONE_EVENTS:
      if (action.name === walkInOut) {
        return update(state, {
          [action.zoneId]: {
            events: { $unset: [action.sessionId] },
          },
        });
      } else if (action.name === driveInOut) {
        let lastSessionId = null;
        if (state[action.zoneId].sessionId) {
          lastSessionId = state[action.zoneId].sessionId;
        }
        return {
          ...state,
          [action.zoneId]: {...initialZoneState[action.name], lastSessionId: lastSessionId },
        };
      }
      if (get(state, [action.zoneId, "timer"])) {
        let lastSessionId = null;
        if (state[action.zoneId].sessionId) {
          lastSessionId = state[action.zoneId].sessionId;
        }
        return {
          ...state,
          [action.zoneId]: {...initialZoneState[action.name], lastSessionId: lastSessionId },
        };
      }
      return state;
    case SET_TIMEOUT:
      if (action.name === walkInOut) {
        return {
          ...state,
          [action.zoneId]: update(state[action.zoneId], {
            events: {
              [action.sessionId]: {
                timeout: { $set: action.timeout },
                cleaningTimestamp: { $set: Date.now() + state.defaultTimer },
                open: { $set: false },
              },
            },
          }),
        };
      }
      return {
        ...state,
        [action.zoneId]: update(state[action.zoneId], {
          timeout: { $set: action.timeout },
        }),
      };
    case ADD_DAC_DETAILS:
      return update(state, {
        [action.zoneId]: {
          events: {
            [action.sessionId]: {
              dacEventGroup: { $set: action.dacEventGroup },
            },
          },
        },
      });
    case ADD_EVENT:
      return {
        ...state,
        [action.zoneId]: addEventToState(
          action.name,
          state[action.zoneId],
          action.newEvent,
          state.defaultTimer
        ),
      };
    case OPEN_CARD:
      if (action.name === walkInOut) {
        return update(state, {
          [action.zoneId]: {
            events: {
              [action.sessionId]: {
                open: { $set: true },
                timeout: { $set: null },
              },
            },
          },
        });
      }
      return state;
    case SET_OPEN:
      if (action.name === walkInOut) {
        return update(state, {
          [action.zoneId]: {
            events: {
              [action.sessionId]: {
                open: { $set: action.open },
              },
            },
          },
        });
      }
      return state;
    case UPDATE_EVENTS:
      if (action.name === walkInOut) {
        return update(state, {
          [action.zoneId]: {
            events: { $set: action.payload },
          },
        });
      }
      return update(state, {
        [action.zoneId]: {
          $set: action.payload,
        },
      });
    default:
      return state;
  }
};

export default liveEvents;

const checkAllAbsent = (zoneState) => {
  let allAbsent = true;
  [driver, truck, trailer, container].forEach((type) => {
    // Check for non absent events already present
    if (zoneState && get(zoneState, [type, "state"]) !== absent) {
      allAbsent = false;
    }
  });
  return allAbsent;
};

const addEventToState = (name, zoneState, newEvent, timer) => {
  if (!zoneState) {
    zoneState = initialZoneState[name];
  }
  if (name === driveInOut) {
    return addEventDriveInOut(zoneState, newEvent, timer);
  } else if (name === walkInOut) {
    return addEventWalkInOut(zoneState, newEvent, timer);
  } else if (name === trainInOut) {
    return addEventTrainInOut(zoneState, newEvent, timer);
  }
};

const addEventTrainInOut = (zoneState, newEvent, timer) => {
  let newState = { ...zoneState };
  // TODO: handle new train live event.  Stub for now.
  return newState;
};

const addEventWalkInOut = (zoneState, newEvent, timer) => {
  if (zoneState.events) {
    let newState = { ...zoneState.events };
    if (newState[newEvent.session_id]) {
      if (newEvent.state === absent) {
        if (timer === 0) {
          delete newState[newEvent.session_id];
        } else {
          newState[newEvent.session_id].timer = timer;
          newState[newEvent.session_id].cleaningTimestamp = Date.now() + timer;
        }
      } else if (newEvent.state === thermal) {
        // Remove dacId to force update
        delete newState[newEvent.session_id].dacId;
      } else if (
        get(zoneState, ["events", newEvent.session_id, "state"]) === detected
      ) {
        // Previous state was detected
        newState[newEvent.session_id] = {
          ...newState[newEvent.session_id],
          ...newEvent,
          apiCallFailed: false,
        };
        delete newState[newEvent.session_id].dac;
        delete newState[newEvent.session_id].dacId;
      } else if (newEvent.state === identified) {
        if (
          get(zoneState, ["events", newEvent.session_id, "state"]) ===
          unidentified
        ) {
          newState[newEvent.session_id] = {
            ...newEvent,
            open: get(zoneState, ["events", newEvent.sessionId, "open"]),
            timer: get(zoneState, ["events", newEvent.sessionId, "timer"]),
          };
        } else {
          let bestProfile = getBestProfile(
            newState[newEvent.session_id],
            newEvent
          );
          // Remove dacId to force update
          newState[newEvent.session_id] = {
            ...newState[newEvent.session_id],
            ...newEvent,
            apiCallFailed: false,
          };
          delete newState[newEvent.session_id].dacId;
          delete newState[newEvent.session_id].profile;
          newState[newEvent.session_id].profile = bestProfile;
        }
      }
    } else if (newEvent.state === detected) {
      newState[newEvent.session_id] = newEvent;
    }
    return update(zoneState, {
      events: { $set: newState },
    });
  }
  return zoneState;
};

const addEventDriveInOut = (zoneState, newEvent, timer) => {
  // Note due to addEventToState, we do not need to check for zoneState.
  // There will always be a zone state.
  let newState = { ...zoneState };

  // Kind of need to figure out what this is in support of. - RDR
  if (newState.waiting) delete newState.waiting;
  if (newState.open) delete newState.open;

  // There was a previous state.
  // Is the new event from the previous session (late event)?
  if (zoneState.lastSessionId === newEvent.session_id) {
    // This event was part of the previous session so we simply ignore it.
    console.log(`Live event from previous session - discarding ${newEvent.type} event.`);
    return newState
  }

  // Is there a session change?
  if (zoneState.sessionId === newEvent.session_id) {
    // We're still in the current state so update time.
    // if it's the vehicle exit, we need to setup the clearing timer
    if (newEvent.type === truck && newEvent.state === absent) {
      // It's the vehicle exit
      // Need to have a quick check to see if the time has already
      // expired.
      let reSendDelta = get(newEvent, 're_send_delta', null);
      if (reSendDelta) {
        reSendDelta = parseInt(reSendDelta);
        // console.debug(`liveEvents: detecting vehicle_exit resend of ${reSendDelta} ms`);
      }
      if (timer === 0 || (reSendDelta && reSendDelta > timer)) {
        // console.debug(`liveEvents: handling vehicle_exit immediate session clear`);
        newState = {...initialZoneState.driveInOut};
        newState.lastSessionId = newEvent.session_id;
      } else {
        let realDelay = timer;
        if (reSendDelta) realDelay = timer - reSendDelta;
        // console.debug(`liveEvents: handling vehicle_exit starting clear delay of ${realDelay} ms`);
        newState.globalState = 0;
        newState.freezeTimer = true;
        newState.cleaningTimestamp = Date.now() + realDelay;
        newState.timer = !checkAllAbsent(zoneState) ? realDelay : null;
      }
      console.log(`Live event is ${newEvent.type} absence - starting clearing timer on session.`);
      return newState;
    }

    // if not we need to update the state
    if (
      // The new event is an identify OR a more recent un-identified (aka don't replace an identifed with an un-identified)
      ( (newEvent.state === unidentified && get(zoneState, [newEvent.type, "state"]) !== identified) ||
        newEvent.state === identified) &&
      // AND we're potentially updating the profile or group id
      ( !get(zoneState, [newEvent.type, "event_group_id"]) ||
        get(newEvent, ["profile", "id"]) === undefined ||
        get(newEvent, ["profile", "id"]) !== get(zoneState, [newEvent.type, "profile", "id"]))
    ) {
      newState[newEvent.type] = {
        ...newState[newEvent.type],
        ...newEvent,
        apiCallFailed: false,
      };
      delete newState[newEvent.type].dacId;
      newState.refreshTimeline = true;
      console.log(`Live event has new information - updating ${newEvent.type} live event card.`);
      return newState;
    }
    if (newEvent.state === supplemental) {
      delete newState[newEvent.type].dacId;
      newState.refreshTimeline = true;
      console.log(`Live event got a supplemental image - updating ${newEvent.type} live event card.`);
      return newState;
    }
    if (newEvent.state === state_reset) {
      newState[newEvent.type] = {
        ...initialZoneState.driveInOut[newEvent.type],
      };
      console.log(`Live event has a remove message - clearing ${newEvent.type} live event card.`);
      return newState;
    }

    // otherwise, discard the event
    console.log(`Live event does not have new information - discarding ${newEvent.type} event.`);
    return newState;
  }

  // There is a session change
  if (zoneState.timeout) {
    clearTimeout(zoneState.timeout);
  }
  let lastSessionId = zoneState.sessionId;
  newState = {...initialZoneState.driveInOut};
  newState.globalState = 1;
  newState.startTime = Date.now();
  newState.sessionId = newEvent.session_id;
  newState.lastSessionId = lastSessionId;
  newState[newEvent.type] = newEvent;
  console.log(`Live event session change - starting with ${newEvent.type} event.`);
  return newState;
};

const getBestProfile = (oldState, newState) => {
  if (get(oldState, ["profile", "id"]) !== get(newState, ["profile", "id"])) {
    return get(newState, ["profile"]);
  } else if (
    parseFloat(get(oldState, ["profile", "confidence"])) >
    parseFloat(get(newState, ["profile", "confidence"]))
  ) {
    return get(oldState, ["profile"]);
  }
  return get(newState, ["profile"]);
};
